import type {
  BannerActionDetailOut,
  BannerSize,
} from '@/api/generated/schemas';
import { cropInfo, isLossy } from '../creative/creative';
import type { CropInfo } from '../creative/creative';
import type { PreviewAlertItem } from '@/components/mobile_preview/PreviewAlertList';

export interface CreativeModel {
  imageUrl: string | null;
  /** Declared container ratio the creative is cropped into. */
  size: BannerSize;
  /** Stored creative dimensions; null when the backend has none. */
  dimensions: { width: number; height: number } | null;
  /** What the declared size crops away; null without dimensions. */
  crop: CropInfo | null;
  /**
   * Whether this action can reach a feed at all. The backend drops
   * imageless actions from every banner that links them.
   */
  rendersInFeed: boolean;
}

export const buildCreativeModel = (
  action: Pick<
    BannerActionDetailOut,
    'image_url' | 'image_width' | 'image_height' | 'size'
  >
): CreativeModel => {
  const { image_width: w, image_height: h } = action;
  const imageUrl = action.image_url || null;

  return {
    imageUrl,
    size: action.size,
    dimensions: w != null && h != null ? { width: w, height: h } : null,
    crop: cropInfo(action, action.size),
    rendersInFeed: imageUrl !== null,
  };
};

/** Suffixes resolve against `user_catalog.banner_actions.preview.alerts.*`. */
export type CreativeAlert = PreviewAlertItem;

/** Only the bad news — a creative that fits its size is the norm. */
export const creativeAlerts = (model: CreativeModel): CreativeAlert[] => {
  if (!model.rendersInFeed) {
    return [{ level: 'error', key: 'no_image', params: {} }];
  }

  if (model.dimensions === null || model.crop === null) {
    return [{ level: 'warning', key: 'unknown_dimensions', params: {} }];
  }

  if (isLossy(model.crop)) {
    return [
      {
        level: 'warning',
        key: 'crop_lossy',
        params: {
          percent: Math.round(model.crop.loss * 100),
          width: model.dimensions.width,
          height: model.dimensions.height,
        },
      },
    ];
  }

  return [];
};
